<template>
  <article v-if="caseStudy" class="case-study">
    <!-- Hero -->
    <section class="case-study-hero">
      <div class="case-study-hero-image">
        <img :src="caseStudy.image" :alt="caseStudy.name" />
      </div>
      <div class="case-study-hero-content">
        <nuxt-link to="/projects" class="case-study-back medium-text-medium" aria-label="Back to all projects">
          <span v-html="icons.rightArrowOnlyIcon"></span>
          <p class="medium-text-medium">All projects</p>
        </nuxt-link>
        <h1 class="extra-large-text-list fade-up-animation">{{ caseStudy.name }}</h1>
        <p class="heading-3 fade-up-animation">{{ caseStudy.tagline }}</p>
        <a
          v-if="caseStudy.link && caseStudy.link !== '#'"
          :href="caseStudy.link"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="`Visit ${caseStudy.name}`"
          class="arrow-button fade-up-animation">
          <p class="list-text-bold">Visit the live site</p>
          <div class="arrow-button-icon" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div>
        </a>
      </div>
    </section>

    <!-- Meta strip -->
    <section class="section-container section-container-wider">
      <div class="case-study-meta fade-up-animation">
        <div class="case-study-meta-item">
          <p class="small-text-list text-uppercase">Year</p>
          <p class="body-text-medium">{{ caseStudy.year }}</p>
        </div>
        <div class="case-study-meta-item">
          <p class="small-text-list text-uppercase">Industry</p>
          <p class="body-text-medium">{{ caseStudy.industry }}</p>
        </div>
        <div class="case-study-meta-item">
          <p class="small-text-list text-uppercase">Platforms</p>
          <p class="body-text-medium">{{ caseStudy.platforms.join(', ') }}</p>
        </div>
        <div class="case-study-meta-item">
          <p class="small-text-list text-uppercase">What we did</p>
          <p class="body-text-medium">{{ caseStudy.services.join(', ') }}</p>
        </div>
      </div>
    </section>

    <!-- Overview -->
    <section class="section-container">
      <div class="case-study-block">
        <div class="section-container-header-box fade-up-animation">
          <p class="list-text">Overview</p>
        </div>
        <div class="section-container-body fade-up-animation">
          <p v-for="paragraph in caseStudy.intro" :key="paragraph" class="body-text">{{ paragraph }}</p>
        </div>
      </div>
    </section>

    <!-- Challenge -->
    <section class="section-container">
      <div class="case-study-block">
        <div class="section-container-header-box fade-up-animation">
          <p class="list-text">The challenge</p>
        </div>
        <div class="section-container-body fade-up-animation">
          <p v-for="paragraph in caseStudy.challenge" :key="paragraph" class="body-text">{{ paragraph }}</p>
        </div>
      </div>
    </section>

    <!-- Approach -->
    <section class="section-container">
      <div class="case-study-block">
        <div class="section-container-header-box fade-up-animation">
          <p class="list-text">What we built</p>
        </div>
        <div class="case-study-approach">
          <div
            v-for="(block, index) in caseStudy.approach"
            :key="block.title"
            class="case-study-approach-item fade-up-animation">
            <p class="heading-4-list disabled-text">{{ String(index + 1).padStart(2, '0') }}</p>
            <div class="case-study-approach-item-body">
              <h3 class="heading-3">{{ block.title }}</h3>
              <p class="body-text">{{ block.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Tech stack -->
    <section class="section-container">
      <div class="case-study-block">
        <div class="section-container-header-box fade-up-animation">
          <p class="list-text">Under the hood</p>
        </div>
        <div class="case-study-tech fade-up-animation">
          <div v-for="tech in caseStudy.tech" :key="tech.name" class="case-study-tech-item">
            <img :src="tech.icon" :alt="tech.name" loading="lazy" />
            <p class="medium-text-list-medium">{{ tech.name }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Outcome -->
    <section class="section-container">
      <div class="case-study-block">
        <div class="section-container-header-box fade-up-animation">
          <p class="list-text">The outcome</p>
        </div>
        <div class="section-container-body fade-up-animation">
          <p v-for="paragraph in caseStudy.outcome" :key="paragraph" class="body-text">{{ paragraph }}</p>
        </div>
        <div
          v-if="caseStudy.highlights && caseStudy.highlights.length"
          class="case-study-highlights fade-up-animation">
          <div v-for="highlight in caseStudy.highlights" :key="highlight" class="case-study-highlights-item">
            <span class="case-study-highlights-item-marker"></span>
            <p class="body-text-medium">{{ highlight }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Next project -->
    <section v-if="nextCaseStudy" class="section-container section-container-wider">
      <p class="case-study-next-label small-text-list text-uppercase fade-up-animation">Next project</p>
      <nuxt-link
        :to="`/projects/${nextCaseStudy.slug}`"
        class="case-study-next fade-up-animation"
        :aria-label="`Next project, ${nextCaseStudy.name}`">
        <div class="case-study-next-image">
          <img :src="nextCaseStudy.image" :alt="nextCaseStudy.name" />
        </div>
        <div class="case-study-next-content">
          <p class="case-study-next-eyebrow small-text-list text-uppercase">{{ nextCaseStudy.industry }}</p>
          <p class="large-text-list case-study-next-name">{{ nextCaseStudy.name }}</p>
          <p class="body-text case-study-next-tagline">{{ nextCaseStudy.tagline }}</p>
        </div>
        <div class="case-study-next-arrow" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div>
      </nuxt-link>
    </section>

    <!-- CTA -->
    <section class="section-container section-container-wider section-container-double-gap">
      <h2 class="section-container-heading">
        <p
          v-for="line in ctaLines"
          :key="line.text"
          class="extra-large-text-list fade-up-animation"
          :class="{ 'special-text': line.special }">
          {{ line.text }}
        </p>
      </h2>
      <nuxt-link class="arrow-button arrow-button-bigger fade-up-animation" to="/#contact" aria-label="Start a project">
        <p class="heading-4-bold">Start a project</p>
        <div class="arrow-button-icon" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div>
      </nuxt-link>
    </section>
  </article>
</template>

<script lang="ts" setup>
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';
import icons from '~/icons';
import { caseStudies, getCaseStudyBySlug } from '@/data/caseStudies';

const route = useRoute();
const slug = computed(() => String(route.params.slug));

const caseStudy = computed(() => getCaseStudyBySlug(slug.value));

if (!caseStudy.value) {
  throw createError({ statusCode: 404, statusMessage: 'Project not found', fatal: true });
}

const nextCaseStudy = computed(() => {
  const index = caseStudies.findIndex((item) => item.slug === slug.value);
  if (index === -1) {
    return undefined;
  }
  return caseStudies[(index + 1) % caseStudies.length];
});

const ctaLines = [
  { text: 'Got something', special: false },
  { text: 'like this in mind?', special: true },
  { text: "Let's build it.", special: false },
];

useFadeUpObserver();

const study = caseStudy.value!;
const pageTitle = `${study.name} | VORTEC`;

useSeo({
  title: pageTitle,
  description: study.short_description,
  path: `/projects/${study.slug}`,
  image: study.image,
  type: 'article',
  jsonLd: [
    {
      '@type': 'CreativeWork',
      name: study.name,
      description: study.short_description,
      url: `https://vortec.io/projects/${study.slug}`,
      image: `https://vortec.io${study.image}`,
      creator: {
        '@type': 'Organization',
        name: 'VORTEC',
        url: 'https://vortec.io',
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://vortec.io' },
        { '@type': 'ListItem', position: 2, name: 'Our Work', item: 'https://vortec.io/projects' },
        { '@type': 'ListItem', position: 3, name: study.name, item: `https://vortec.io/projects/${study.slug}` },
      ],
    },
  ],
});
</script>
